import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'bd_geo';
const TTL_IP_MS = 7 * 24 * 60 * 60 * 1000;        // 7 days for auto-detected
const TTL_MANUAL_MS = 180 * 24 * 60 * 60 * 1000;  // ~6 months for user choice

function readCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || !data.state) return null;
    const ttl = data.manual ? TTL_MANUAL_MS : TTL_IP_MS;
    if (Date.now() - (data.ts || 0) > ttl) return null;
    return data;
  } catch {
    return null;
  }
}

function writeCache(payload) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...payload, ts: Date.now() }));
  } catch {
    /* ignore quota errors */
  }
}

/**
 * useGeoLocation — non-blocking, IP-based city/state suggestion.
 *
 * - Reads localStorage first; returns immediately if a valid entry exists.
 * - When no cache (or expired), fires a fire-and-forget GET /api/public/geo-detect
 *   with a 3.5s axios timeout. Failures are silent (returned object stays
 *   `ready=true, detected=null`).
 * - `persistManual({city, state})` records the user's explicit choice so it
 *   survives across visits with a 6 month TTL.
 *
 * Returns `{ detected, ready, source, persistManual }`.
 */
export default function useGeoLocation() {
  const [detected, setDetected] = useState(() => readCache());
  const [ready, setReady] = useState(() => !!readCache());
  const fetchedRef = useRef(false); // make sure we never refetch in the same mount

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const cached = readCache();
    if (cached) {
      setDetected(cached);
      setReady(true);
      return;
    }

    let cancelled = false;
    const ctrl = new AbortController();
    axios
      .get(`${API_URL}/api/public/geo-detect`, { timeout: 3500, signal: ctrl.signal })
      .then((res) => {
        if (cancelled) return;
        const d = res.data || {};
        if (d.ok && d.state) {
          const payload = { city: d.city || null, state: d.state, manual: false, source: d.source || 'ip' };
          writeCache(payload);
          setDetected(payload);
        } else {
          setDetected(null);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setDetected(null);
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, []);

  const persistManual = useCallback(({ city, state }) => {
    if (!state) return;
    const payload = { city: city || null, state, manual: true, source: 'manual' };
    writeCache(payload);
    setDetected(payload);
  }, []);

  return { detected, ready, source: detected?.source, persistManual };
}
